import { AnswerApi } from 'api/AnswerApi';
import { AxiosInstance } from 'axios';
import { AccessDeniedException } from 'exception/AccessDeniedException';
import { EntityNotFoundException } from 'exception/EntityNotFoundException';
import { NoAvailableSurveysException } from 'exception/NoAvailableSurveysException';
import { SurveyAlreadyCompletedException } from 'exception/SurveyAlreadyCompletedException';
import { SurveyClosedException } from 'exception/SurveyClosedException';
import { SurveyInDevelopmentException } from 'exception/SurveyInDevelopmentException';
import { UnauthorizedException } from 'exception/UnauthorizedException';
import { AccessCodeMapper } from 'mapper/AccessCodeMapper';
import { AnswerMapper } from 'mapper/AnswerMapper';
import { ProjectMapper } from 'mapper/ProjectMapper';
import { AccessCode } from 'model/AccessCode';
import { Answer } from 'model/Answer';
import { Project } from 'model/Project';
import { selectApiData, throwMappedApiError } from 'selector/apiSelector';
import { selectResponseData, throwErrorData } from 'selector/axiosSelector';
import { Meta } from 'model/Meta';
import { MetaMapper } from 'mapper/MetaMapper';
import { mapRespondentMetaDtoToModel, RespondentMetaModel } from 'api/dto/RespondentMeta.dto';
import { AvailableSurveyNew } from 'model/AvailableSurveyNew';
import { i18n } from 'translations/i18n';

export namespace SurveyApi {
  export type SavePost = {
    answers: AnswerApi.Entry[];
    token?: string;
    submitId?: string;
  };
  export type SubmitPost = SavePost;
  export type SaveQuery = {
    save: boolean;
    continueEmail?: string;
  };

  export type AvailableSurveyNewEntry = {
    name: string;
    slug: string;
  };
}

export class SurveyApi {
  private readClient: AxiosInstance;
  private writeClient: AxiosInstance;
  private beaconClient: (path: string, data: unknown) => boolean;
  private answerMapper: AnswerMapper;
  private projectMapper: ProjectMapper;
  private accessCodeMapper: AccessCodeMapper;
  private metaMapper: MetaMapper;
  private surveyCache: Map<string, Promise<Project>> = new Map();

  constructor(
    readClient: AxiosInstance,
    writeClient: AxiosInstance,
    beaconClient: (path: string, data: unknown) => boolean,
    answerMapper: AnswerMapper,
    projectMapper: ProjectMapper,
    accessCodeMapper: AccessCodeMapper,
    metaMapper: MetaMapper
  ) {
    this.accessCodeMapper = accessCodeMapper;
    this.projectMapper = projectMapper;
    this.answerMapper = answerMapper;
    this.metaMapper = metaMapper;
    this.beaconClient = beaconClient;
    this.readClient = readClient;
    this.writeClient = writeClient;
  }

  get(distributionSlug: string, accessCode?: string, submitId?: string): Promise<Project> {
    const language = i18n.language ?? '';
    const cacheKey = `${distributionSlug}|${accessCode ?? ''}|${submitId ?? ''}|${language}`;
    const cached = this.surveyCache.get(cacheKey);
    if (cached) return cached;

    const request = this.readClient
      .get(
        `/api/v1/survey/${distributionSlug}` +
          (accessCode ? `/${accessCode}` : '') +
          (submitId ? `?submitId=${submitId}` : '')
      )
      .then(selectApiData)
      .then((entry) => this.projectMapper.deserialize(entry))
      .catch((error) => {
        this.surveyCache.delete(cacheKey);
        throw error;
      })
      .catch(
        throwMappedApiError([
          SurveyInDevelopmentException,
          SurveyClosedException,
          SurveyAlreadyCompletedException,
          AccessDeniedException,
          UnauthorizedException,
        ])
      );

    this.surveyCache.set(cacheKey, request);
    return request;
  }

  getQuestions(distributionSlug: string, accessCode?: string, submitId?: string): Promise<Project> {
    const path = accessCode
      ? `/api/v1/survey/${distributionSlug}/${accessCode}/questions`
      : `/api/v1/survey/${distributionSlug}/questions` + (submitId ? `?submitId=${submitId}` : '');

    return this.readClient
      .get(path)
      .then(selectApiData)
      .then((entry) => this.projectMapper.deserializeQuestions(entry))
      .catch(
        throwMappedApiError([
          SurveyInDevelopmentException,
          SurveyClosedException,
          SurveyAlreadyCompletedException,
          AccessDeniedException,
          UnauthorizedException,
        ])
      );
  }

  previewQuestions(distributionSlug: string, query: string): Promise<Project> {
    return this.readClient
      .get(`/api/v1/survey/${distributionSlug}/preview/questions?${query}`)
      .then(selectApiData)
      .then((entry) => this.projectMapper.deserializeQuestions(entry))
      .catch(
        throwMappedApiError([
          SurveyInDevelopmentException,
          SurveyClosedException,
          SurveyAlreadyCompletedException,
          AccessDeniedException,
          UnauthorizedException,
          EntityNotFoundException,
        ])
      );
  }

  clearSurveyCache(): void {
    this.surveyCache.clear();
  }

  preview(distributionSlug: string, query: string): Promise<Project> {
    return this.readClient
      .get(`/api/v1/survey/${distributionSlug}/preview?${query}`)
      .then(selectApiData)
      .then((entry) => this.projectMapper.deserialize(entry))
      .catch(
        throwMappedApiError([
          SurveyInDevelopmentException,
          SurveyClosedException,
          SurveyAlreadyCompletedException,
          AccessDeniedException,
          UnauthorizedException,
        ])
      );
  }

  getAvailableSurveysNew(
    distributionSlug: string,
    accessCode: string
  ): Promise<AvailableSurveyNew[]> {
    return this.readClient
      .get<{ data: SurveyApi.AvailableSurveyNewEntry[] }>(
        `/api/v1/survey-access/${distributionSlug}/available-surveys?accessCode=${accessCode}`
      )
      .then(selectApiData)
      .catch(
        throwMappedApiError([
          SurveyInDevelopmentException,
          SurveyClosedException,
          SurveyAlreadyCompletedException,
          AccessDeniedException,
          UnauthorizedException,
          NoAvailableSurveysException,
        ])
      );
  }

  save(
    distributionSlug: string,
    answers: Answer[],
    accessCode?: string,
    continueEmail?: string,
    useBeacon = false,
    submitId?: string,
    token?: string
  ): Promise<any> {
    const data: SurveyApi.SavePost = {
      answers: answers.map((answer) => this.answerMapper.serialize(answer)),
      submitId: submitId,
      token: token,
    };
    const query = new URLSearchParams({
      save: 'true',
      ...(continueEmail && { continueEmail }),
    }).toString();

    const path =
      `/api/v1/survey/${distributionSlug}` + (accessCode ? `/${accessCode}` : '') + `?${query}`;

    if (useBeacon) {
      // in order to send request to the server after page unload, we need to use beacon client
      this.beaconClient(path, data);

      return Promise.resolve();
    } else {
      return this.writeClient.post(path, data).catch((error) => throwErrorData(error, 'errors'));
    }
  }

  submit(
    distributionSlug: string,
    answers: Answer[],
    accessCode?: string,
    submitId?: string,
    token?: string
  ): Promise<string> {
    const data: SurveyApi.SubmitPost = {
      answers: answers.map((answer) => this.answerMapper.serialize(answer)),
      token: token,
      submitId,
    };

    return this.writeClient
      .post(`/api/v1/survey/${distributionSlug}` + (accessCode ? `/${accessCode}` : ''), data)
      .then(() => distributionSlug)
      .catch((error) => throwErrorData(error, 'errors'));
  }

  unsubscribe(distributionSlug: string, accessCode: string): Promise<AccessCode> {
    return this.writeClient
      .post(`/api/v1/survey/${distributionSlug}/${accessCode}/unsubscribe`)
      .then((response) => selectResponseData(response, 'data'))
      .then((entry) => this.accessCodeMapper.deserialize(entry))
      .catch((error) => throwErrorData(error, 'errors'));
  }

  fetchAccessCodeInformation(distributionId: string, accessCode: string): Promise<AccessCode> {
    return this.readClient
      .get(`/api/v1/survey/${distributionId}/${accessCode}/access-code`)
      .then((response) => selectResponseData(response, 'data'))
      .then((entry) => this.accessCodeMapper.deserialize(entry))
      .catch((error) => throwErrorData(error, 'errors'));
  }

  getMeta(distributionSlug: string): Promise<Meta> {
    return this.readClient
      .get(`/api/v1/survey/${distributionSlug}/meta`)
      .then(selectApiData)
      .then((entry) => this.metaMapper.deserialize(entry))
      .catch((error) => throwErrorData(error, 'errors'));
  }

  getRespondentMeta(distributionSlug: string, kioskCode: string): Promise<RespondentMetaModel> {
    return this.readClient
      .get(`/api/v1/survey/${distributionSlug}/respondent/${kioskCode}/meta`)
      .then(selectApiData)
      .then(mapRespondentMetaDtoToModel)
      .catch((error) => throwErrorData(error, 'errors'));
  }
}
